import { useState, useMemo } from 'react'
import { useNavigate } from 'react-router-dom'
import { useMarkets, useTriggerSync, useUpdateMarket } from '../hooks/useSDI'
import { useDeleteMarket } from '../hooks/useSDIExtended'
import { PageHeader } from '@/shared/components/ui/PageHeader'
import { KpiCard } from '@/shared/components/ui/KpiCard'
import { Badge } from '@/shared/components/ui/Badge'
import { Button } from '@/shared/components/ui/Button'
import { EmptyState } from '@/shared/components/ui/EmptyState'
import { FilterBar, type FilterState } from '../components/sdi/shared/FilterBar'
import { MarketCreateModal } from '../components/sdi/markets/MarketCreateModal'
import { MarketActionMenu, SyncProgressIndicator, DeleteMarketModal } from '../components/sdi/markets/MarketActions'
import type { MarketListItem } from '../hooks/sdi.types'
import {
  Plus, Download, ChevronRight, Columns,
} from 'lucide-react'
import { cn } from '@/shared/lib/utils'

/* eslint-disable @typescript-eslint/no-explicit-any */

const fmt = new Intl.NumberFormat('fr-FR')
const fmtCompact = new Intl.NumberFormat('fr-FR', { notation: 'compact', maximumFractionDigits: 1 })
const fmtPct = new Intl.NumberFormat('fr-FR', { style: 'percent', minimumFractionDigits: 1 })
const fmtEur = new Intl.NumberFormat('fr-FR', { notation: 'compact', style: 'currency', currency: 'EUR', maximumFractionDigits: 1 })

type ColKey = 'country' | 'keywords' | 'market_size' | 'capture' | 'lost' | 'confidence' | 'status'
const ALL_COLS: { key: ColKey; label: string; defaultVisible: boolean }[] = [
  { key: 'country', label: 'Pays', defaultVisible: true },
  { key: 'keywords', label: 'Mots-clés', defaultVisible: true },
  { key: 'market_size', label: 'Demande', defaultVisible: true },
  { key: 'capture', label: 'Capture', defaultVisible: true },
  { key: 'lost', label: 'Opportunité', defaultVisible: true },
  { key: 'confidence', label: 'Confiance', defaultVisible: false },
  { key: 'status', label: 'Statut', defaultVisible: true },
]

function loadVisibleCols(): Set<ColKey> {
  try { const s = localStorage.getItem('sdi_markets_columns'); if (s) return new Set(JSON.parse(s)) } catch { /* noop */ }
  return new Set(ALL_COLS.filter((c) => c.defaultVisible).map((c) => c.key))
}

export function MarketsListPage() {
  const navigate = useNavigate()
  const [filterState, setFilterState] = useState<FilterState>({ period: '30d', country: '', cluster: '', source: '', status: '' })
  const { data: response, isLoading } = useMarkets({ status: filterState.status || undefined })
  const triggerSync = useTriggerSync()
  const updateMarket = useUpdateMarket()
  const deleteMarket = useDeleteMarket()

  const [showCreate, setShowCreate] = useState(false)
  const [selected, setSelected] = useState<Set<string>>(new Set())
  const [visibleCols, setVisibleCols] = useState<Set<ColKey>>(loadVisibleCols)
  const [showColPicker, setShowColPicker] = useState(false)
  const [deleteTarget, setDeleteTarget] = useState<MarketListItem | null>(null)

  const markets = (response?.data ?? []) as MarketListItem[]
  const aggregates = (response as any)?.meta?.aggregates
  const syncingCount = markets.filter((m) => m.status === 'syncing').length
  const countries = useMemo(() => [...new Set(markets.map((m) => m.country))].sort(), [markets])

  const toggleCol = (key: ColKey) => {
    setVisibleCols((prev) => { const n = new Set(prev); n.has(key) ? n.delete(key) : n.add(key); localStorage.setItem('sdi_markets_columns', JSON.stringify([...n])); return n })
  }
  const handleArchive = (id: string) => updateMarket.mutate({ id, status: 'archived' })
  const handleDelete = () => { if (deleteTarget) { deleteMarket.mutate(deleteTarget.id); setDeleteTarget(null) } }
  const toggleSelect = (id: string) => { setSelected((p) => { const n = new Set(p); n.has(id) ? n.delete(id) : n.add(id); return n }) }

  const exportCsv = () => {
    const header = ['Nom', 'Pays', 'Keywords', 'Demande', 'Capture', 'Opportunité', 'Statut']
    const rows = markets.map((m) => [m.name, m.country, m.keyword_count, m.market_size ?? '', m.capture_rate ?? '', m.lost_value ?? '', m.status])
    const blob = new Blob([[header, ...rows].map((r) => r.join(',')).join('\n')], { type: 'text/csv' })
    const a = document.createElement('a'); a.href = URL.createObjectURL(blob); a.download = `sdi_markets_${new Date().toISOString().slice(0, 10)}.csv`; a.click()
  }

  return (
    <div className="space-y-6">
      <PageHeader
        moduleId="sdi"
        moduleFullName="Search Demand Intelligence"
        title="Marchés"
        subtitle={`${markets.length} marché(s) configuré(s)`}
        actions={
          <div className="flex items-center gap-2">
            <Button variant="secondary" size="sm" onClick={exportCsv}><Download size={14} /> Export</Button>
            <Button onClick={() => setShowCreate(true)}><Plus size={16} /> Nouveau marché</Button>
          </div>
        }
      />

      <FilterBar countries={countries} onFilterChange={setFilterState} />

      {/* KPI Strip */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <KpiCard label="Marchés actifs" value={aggregates?.total_markets ?? 0} accentColor="#0F7173" loading={isLoading} variant="md" />
        <KpiCard label="Demande totale" value={fmtCompact.format(aggregates?.total_market_size ?? 0)} accentColor="#0F7173" loading={isLoading} variant="md" />
        <KpiCard label="Capture moyenne" value={aggregates?.avg_capture_rate ? fmtPct.format(aggregates.avg_capture_rate) : '—'} accentColor="#1E6B3A" loading={isLoading} variant="md" />
        <KpiCard label="Opportunité perdue" value={fmtEur.format(aggregates?.total_lost_value ?? 0)} accentColor="#922B21" loading={isLoading} variant="md" />
      </div>

      {/* Bulk Actions */}
      {selected.size > 0 && (
        <div className="flex items-center gap-3 bg-gold-light border border-gold/20 rounded-card px-4 py-2.5 animate-slide-up">
          <span className="text-[12px] font-medium text-gold-dark">{selected.size} marché(s) sélectionné(s)</span>
          <div className="flex items-center gap-2 ml-auto">
            <Button variant="gold" size="sm" onClick={() => selected.forEach((id) => triggerSync.mutate(id))}>Sync tous</Button>
            <Button variant="ghost" size="sm" onClick={() => selected.forEach((id) => handleArchive(id))}>Archiver</Button>
            <Button variant="ghost" size="sm" onClick={() => setSelected(new Set())}>Annuler</Button>
          </div>
        </div>
      )}

      <SyncProgressIndicator syncingCount={syncingCount} />

      {/* Table or Empty */}
      {!isLoading && markets.length === 0 ? (
        <EmptyState
          title={filterState.status ? 'Aucun marché ne correspond' : 'Aucun marché configuré'}
          description={filterState.status ? 'Essayez de modifier vos filtres.' : 'Créez votre premier marché pour commencer l\'analyse de la demande.'}
          action={{
            label: filterState.status ? 'Réinitialiser les filtres' : '+ Créer un marché',
            onClick: () => filterState.status ? setFilterState({ ...filterState, status: '' }) : setShowCreate(true),
            variant: filterState.status ? 'ghost' : 'primary',
          }}
        />
      ) : (
        <div className="bg-white rounded-card border border-ink-border shadow-card overflow-hidden">
          {/* Column picker */}
          <div className="flex items-center justify-end px-4 py-2 border-b border-ink-border/30">
            <div className="relative">
              <button onClick={() => setShowColPicker(!showColPicker)} className="flex items-center gap-1 text-[11px] text-ink-faint hover:text-ink"><Columns size={13} /> Colonnes</button>
              {showColPicker && (
                <><div className="fixed inset-0 z-20" onClick={() => setShowColPicker(false)} />
                <div className="absolute right-0 top-full mt-1 z-30 w-44 bg-paper border border-ink-border rounded-card shadow-elevated py-2 animate-fade-in">
                  {ALL_COLS.map((col) => (
                    <label key={col.key} className="flex items-center gap-2 px-3 py-1.5 text-[11px] hover:bg-gold-light/30 cursor-pointer">
                      <input type="checkbox" checked={visibleCols.has(col.key)} onChange={() => toggleCol(col.key)} className="rounded accent-gold" />{col.label}
                    </label>
                  ))}
                </div></>
              )}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-onyx text-paper">
                  <th className="px-3 py-3 w-8"><input type="checkbox" className="rounded accent-gold" onChange={(e) => e.target.checked ? setSelected(new Set(markets.map((m) => m.id))) : setSelected(new Set())} /></th>
                  <th className="text-left px-4 py-3 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">Nom</th>
                  {visibleCols.has('country') && <th className="text-left px-4 py-3 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">Pays</th>}
                  {visibleCols.has('keywords') && <th className="text-right px-4 py-3 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">Mots-clés</th>}
                  {visibleCols.has('market_size') && <th className="text-right px-4 py-3 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">Demande</th>}
                  {visibleCols.has('capture') && <th className="text-right px-4 py-3 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">Capture</th>}
                  {visibleCols.has('lost') && <th className="text-right px-4 py-3 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">Opportunité</th>}
                  {visibleCols.has('confidence') && <th className="text-right px-4 py-3 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">Confiance</th>}
                  {visibleCols.has('status') && <th className="text-center px-4 py-3 text-[9px] font-medium tracking-[0.14em] uppercase text-paper/50">Statut</th>}
                  <th className="px-4 py-3 w-12" />
                </tr>
              </thead>
              <tbody>
                {isLoading ? Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i} className="animate-pulse">{Array.from({ length: 9 }).map((_, j) => (<td key={j} className="px-4 py-3"><div className="h-4 bg-paper-warm rounded w-3/4" /></td>))}</tr>
                )) : markets.map((m, idx) => (
                  <tr key={m.id} onClick={() => navigate(`/sdi/markets/${m.id}`)}
                    className={cn('cursor-pointer hover:bg-gold-light/30 transition-colors', idx % 2 === 1 && 'bg-paper', selected.has(m.id) && 'bg-gold-light/50')}>
                    <td className="px-3 py-3 border-t border-ink-border/20" onClick={(e) => e.stopPropagation()}>
                      <input type="checkbox" checked={selected.has(m.id)} onChange={() => toggleSelect(m.id)} className="rounded accent-gold" />
                    </td>
                    <td className="px-4 py-3 border-t border-ink-border/20">
                      <p className="text-[13px] font-medium text-ink">{m.name}</p>
                      {m.seed_keyword && <p className="text-[10px] font-light text-ink-faint mt-0.5">{m.seed_keyword}</p>}
                    </td>
                    {visibleCols.has('country') && <td className="px-4 py-3 text-[13px] text-ink border-t border-ink-border/20">{m.country}</td>}
                    {visibleCols.has('keywords') && <td className="px-4 py-3 text-right text-[13px] text-ink border-t border-ink-border/20">{fmt.format(m.keyword_count)}</td>}
                    {visibleCols.has('market_size') && <td className="px-4 py-3 text-right text-kpi text-sm text-ink border-t border-ink-border/20">{m.market_size != null ? fmtCompact.format(m.market_size) : '—'}</td>}
                    {visibleCols.has('capture') && (
                      <td className="px-4 py-3 text-right border-t border-ink-border/20">
                        {m.capture_rate != null ? (
                          <span className={cn('text-kpi text-sm', m.capture_rate < 0.15 ? 'text-semantic-lost' : m.capture_rate < 0.30 ? 'text-semantic-warning' : 'text-semantic-capture')}>
                            {fmtPct.format(m.capture_rate)}
                          </span>
                        ) : '—'}
                      </td>
                    )}
                    {visibleCols.has('lost') && <td className="px-4 py-3 text-right text-kpi text-sm text-semantic-lost border-t border-ink-border/20">{m.lost_value != null ? fmtEur.format(m.lost_value) : '—'}</td>}
                    {visibleCols.has('confidence') && (
                      <td className="px-4 py-3 text-right border-t border-ink-border/20">
                        {m.confidence_score != null ? (
                          <Badge variant={m.confidence_score >= 70 ? 'active' : m.confidence_score >= 40 ? 'syncing' : 'error'}>{m.confidence_score.toFixed(0)}</Badge>
                        ) : '—'}
                      </td>
                    )}
                    {visibleCols.has('status') && (
                      <td className="px-4 py-3 text-center border-t border-ink-border/20">
                        <Badge variant={m.status as 'active' | 'draft' | 'syncing' | 'error' | 'archived'}>{m.status}</Badge>
                      </td>
                    )}
                    <td className="px-4 py-3 border-t border-ink-border/20" onClick={(e) => e.stopPropagation()}>
                      <MarketActionMenu onEdit={() => navigate(`/sdi/markets/${m.id}/setup`)} onSync={() => triggerSync.mutate(m.id)}
                        onArchive={() => handleArchive(m.id)} onDelete={() => setDeleteTarget(m)} isSyncing={m.status === 'syncing'} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      <MarketCreateModal open={showCreate} onClose={() => setShowCreate(false)} />
      {deleteTarget && <DeleteMarketModal open={!!deleteTarget} marketName={deleteTarget.name} onConfirm={handleDelete} onClose={() => setDeleteTarget(null)} />}
    </div>
  )
}
